import { FilterDefinition, FilterItemValues, FilterKey, FilterSelection, FilterValueId } from "../types/filters";
import {
    getLabels,
    isDone,
    isHighPriority,
    isLowPriority,
    Job,
    PROGRESS_STATUS_ACCEPTED,
    PROGRESS_STATUS_IN_PROGRESS,
    PROGRESS_STATUS_ISSUE,
    PROGRESS_STATUS_REJECTED,
    PROGRESS_STATUS_SCHEDULED,
    PROGRESS_STATUS_SUCCESS,
    PROGRESS_STATUS_UNASSIGNED,
} from "../types/job";
import { getFilterProgressStatus, imageEntriesFromJob } from "./jobUtils";
import { hasTimeWindow } from "./timeWindow";
import { isAfterTimeOnDay } from "./zonedTime";
import { activeFilterKeys, passesFilters } from "./filters";

export const JOB_FILTER = {
    STATUS: "status",
    LATE: "late",
    PRIORITY: "priority",
    LABEL: "label",
    ASSIGNEE: "assignee",
    TEAM: "team",
    PROOF: "proof",
} as const;

/** The job filters the server can apply to a whole range (see runtime JobQueryFilters); the rest run on what comes back. */
export const SERVER_JOB_FILTER_KEYS: string[] = [JOB_FILTER.STATUS, JOB_FILTER.ASSIGNEE, JOB_FILTER.PRIORITY, JOB_FILTER.LABEL, JOB_FILTER.TEAM];

export const JOB_STATUS = {
    UNASSIGNED: "unassigned",
    SCHEDULED: "scheduled",
    ACCEPTED: "accepted",
    IN_PROGRESS: "inprogress",
    DONE: "done",
    ISSUE: "issue",
    REJECTED: "rejected",
} as const;

export const JOB_LATE = {
    /** the time window ended and nobody has started the job */
    MISSED: "missed",
    /** on the way, but the ETA is after the window ends */
    AT_RISK: "atrisk",
} as const;

export const JOB_PROOF = {
    FORM: "form",
    NO_FORM: "noform",
    SIGNATURE: "signature",
    NO_SIGNATURE: "nosignature",
    PHOTO: "photo",
    NO_PHOTO: "nophoto",
} as const;

export const NO_ASSIGNEE = "";
export const NO_JOB_TEAM = "none";

const STATUS_BY_PROGRESS: Record<number, string> = {
    [PROGRESS_STATUS_UNASSIGNED]: JOB_STATUS.UNASSIGNED,
    [PROGRESS_STATUS_SCHEDULED]: JOB_STATUS.SCHEDULED,
    [PROGRESS_STATUS_ACCEPTED]: JOB_STATUS.ACCEPTED,
    [PROGRESS_STATUS_IN_PROGRESS]: JOB_STATUS.IN_PROGRESS,
    [PROGRESS_STATUS_SUCCESS]: JOB_STATUS.DONE,
    [PROGRESS_STATUS_ISSUE]: JOB_STATUS.ISSUE,
    [PROGRESS_STATUS_REJECTED]: JOB_STATUS.REJECTED,
};

export function jobStatusKey(job: Job): string {
    return STATUS_BY_PROGRESS[getFilterProgressStatus(job)] ?? JOB_STATUS.SCHEDULED;
}

/** The job's window end as hhmm when it has a window and a day, else undefined. */
export function windowEnd(job: Job): number | undefined {
    if (!job.day || !hasTimeWindow(job.windowStart, job.windowEnd) || !(job.windowEnd > 0 && job.windowEnd < 2400)) {
        return undefined;
    }
    return job.windowEnd;
}

/**
 * Late or at risk, from the time window, the check-in and the ETA of the trip to the job. Times are
 * read on the account's clock, like the job tooltip's "past window".
 */
export function jobLateKeys(job: Job, now: number, etaTs?: number, timezone?: string): string[] {
    const end = windowEnd(job);
    if (end === undefined) {
        return [];
    }
    // once the worker got there (the check-in, or the end of a job done without one), that arrival decides for good:
    // a job reached after its window missed it, done or not; nobody's job misses its window all the same
    const checkedIn = job.tsCheckIn > job.tsScheduled ? job.tsCheckIn : 0;
    const arrived = checkedIn || (isDone(job) ? Math.max(job.tsDoneSuccess || 0, job.tsDoneFailed || 0) : 0);
    if (arrived > 0) {
        return isAfterTimeOnDay(arrived, job.day, end, timezone) ? [JOB_LATE.MISSED] : [];
    }
    if (isAfterTimeOnDay(now, job.day, end, timezone)) {
        return [JOB_LATE.MISSED];
    }
    if (etaTs && isAfterTimeOnDay(etaTs, job.day, end, timezone)) {
        return [JOB_LATE.AT_RISK];
    }
    return [];
}

export function jobPriorityKey(job: Job): string {
    return isHighPriority(job) ? "high" : isLowPriority(job) ? "low" : "normal";
}

/** Labels are matched by their text, so the same label in two jobs is one value. */
export function labelKey(text: string): string {
    return text.trim().toLowerCase();
}

function hasSubmittedForm(job: Job): boolean {
    const forms = job.submittedForms?.trim();
    return !!forms && forms !== "[]" && forms !== "{}";
}

function hasPhoto(job: Job): boolean {
    return imageEntriesFromJob(job).length > 0;
}

export function jobProofKeys(job: Job): string[] {
    return [
        hasSubmittedForm(job) ? JOB_PROOF.FORM : JOB_PROOF.NO_FORM,
        job.tsSignature > 0 || !!job.signatureSvg || !!job.hasSignatureSvg ? JOB_PROOF.SIGNATURE : JOB_PROOF.NO_SIGNATURE,
        hasPhoto(job) ? JOB_PROOF.PHOTO : JOB_PROOF.NO_PHOTO,
    ];
}

export type JobFilterContext = {
    now: number;
    /** the account's time zone; the browser's when missing */
    timezone?: string;
    /** ETAs of the trips under way, by job id; only read for the Late filter */
    etaByJobId?: Map<string, number>;
};

export function jobFilterValues(job: Job, context: JobFilterContext, keys?: FilterKey[]): FilterItemValues {
    const wants = (key: string) => !keys || keys.includes(key);
    const values: FilterItemValues = {};
    if (wants(JOB_FILTER.STATUS)) {
        values[JOB_FILTER.STATUS] = [jobStatusKey(job)];
    }
    if (wants(JOB_FILTER.LATE)) {
        values[JOB_FILTER.LATE] = jobLateKeys(job, context.now, context.etaByJobId?.get(job.id), context.timezone);
    }
    if (wants(JOB_FILTER.PRIORITY)) {
        values[JOB_FILTER.PRIORITY] = [jobPriorityKey(job)];
    }
    if (wants(JOB_FILTER.LABEL)) {
        values[JOB_FILTER.LABEL] = getLabels(job).map(label => labelKey(label.text)).filter(text => text.length > 0);
    }
    if (wants(JOB_FILTER.ASSIGNEE)) {
        values[JOB_FILTER.ASSIGNEE] = [job.receiverUid || NO_ASSIGNEE];
    }
    if (wants(JOB_FILTER.TEAM)) {
        values[JOB_FILTER.TEAM] = [job.teamId > 0 ? String(job.teamId) : NO_JOB_TEAM];
    }
    if (wants(JOB_FILTER.PROOF)) {
        values[JOB_FILTER.PROOF] = jobProofKeys(job);
    }
    return values;
}

/** What the dispatch board shows: people's routes, the Unassigned card, or a card with any job (All jobs, Pinned). */
export interface DispatchJobsShown {
    routes: boolean;
    unassigned: boolean;
    any: boolean;
}

/**
 * The dispatch board's Jobs bar offers only what tells the jobs it shows apart. Jobs in a route all
 * have their person, so Unassigned, Assignee and Team say nothing there; the Unassigned card's jobs
 * are all nobody's, so Assignee says nothing there and Status only tells the turned-down ones apart.
 * A picked value always stays, so its chip shows and can be taken off.
 */
export function dispatchJobFilterDefinitions(definitions: FilterDefinition[], selection: FilterSelection, shown: DispatchJobsShown): FilterDefinition[] {
    if (shown.any) {
        return definitions;
    }
    const fits = (key: FilterKey, value: FilterValueId): boolean => {
        switch (key) {
            case JOB_FILTER.STATUS:
                // a turned-down job stays Rejected in a route and once it went back to nobody
                return value === JOB_STATUS.UNASSIGNED ? shown.unassigned
                    : value === JOB_STATUS.REJECTED ? shown.routes || shown.unassigned
                        : shown.routes;
            case JOB_FILTER.ASSIGNEE:
                return false;
            case JOB_FILTER.TEAM:
                return shown.unassigned;
            default:
                return true;
        }
    };
    return definitions.flatMap(definition => {
        const picked = selection.values[definition.key] ?? [];
        const options = definition.options.filter(option => fits(definition.key, option.id) || picked.includes(option.id));
        // cut down to one value or none, the filter has nothing left to tell apart
        const cut = options.length < definition.options.length;
        return cut && options.length <= 1 && picked.length === 0 ? [] : [{ ...definition, options }];
    });
}

/** For lists of jobs: computes only the values the active filters need. */
export function jobPassesFilters(job: Job, selection: FilterSelection, context: JobFilterContext): boolean {
    const keys = activeFilterKeys(selection);
    return keys.length === 0 || passesFilters(jobFilterValues(job, context, keys), selection);
}
